import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

// APIs
import { GetDepartments } from "../../../api/masters/departmentAPI";
import { GetConsultantSpecializations } from "../../../api/masters/consultantSpecializationAPI";
import { BulkImportConsultant, StoreConsultant } from "../../../api/masters/consultantAPI";
import { GetCities } from "../../../api/masters/cityAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// ✅ Validation Schema (aligned with your Mongoose model)
const schema = yup.object().shape({
  name: yup.string().required("Consultant name is required"),
  departmentId: yup.string().required("Department is required"),
  consultantSpecializationId: yup.string().required("Specialization is required"),
  nature: yup.string().required("Nature is required"),
  mobileNo: yup.string().nullable().optional(),
  email: yup.string().nullable(),
  opdCharges: yup.number().typeError("OPD Charges must be a number").min(0, "OPD Charge cannot be negative").nullable(),
  casualtyCharges: yup.number().typeError("Consultation Charges must be a number").min(0, "Consultation Charge cannot be negative").nullable(),
  opdValidUpTo: yup.number().typeError("OPD Valid Up To must be a number").min(0, "OPD Valid Up To cannot be negative").nullable(),
  opdChargesBetweenValidityPeriod: yup.number().typeError("OPD Charges between Validity Period must be a number").min(0, "OPD Charges between Validity Period cannot be negative").nullable(),
  active: yup.string().oneOf(["Yes", "No"]).required(),
  clinics: yup.array().of(
    yup.object().shape({
      site: yup.string().required("Clinic site name is required"),
      timingFrom: yup.string().required("From Timing is required"),
      timingTo: yup.string().required("To Timing is required"),
      phone: yup.string(),
      cityId: yup.string().required("City is required"),
      address1: yup.string().required("Address line 1 is required"),
      address2: yup.string().nullable(),
      address3: yup.string().nullable(),
    })
  ),
});

const AddConsultant = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [cities, setCities] = useState([]);

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      active: "Yes",
      opdCharges: 0,
      casualtyCharges: 0,
      opdValidUpTo: 0,
      opdChargesBetweenValidityPeriod: 0,
      clinics: [],
    },
  });

  // useFieldArray for multiple clinics
  const { fields, append, remove } = useFieldArray({
    control,
    name: "clinics",
  });

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  const fetchSpecializations = async () => {
    try {
      const res = await GetConsultantSpecializations();
      if (res.success) setSpecializations(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };
  
  const fetchCities = async () => {
    try {
      const res = await GetCities();
      if (res.success) setCities(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: "LOADER_START" });

        await fetchDepartments();
        await fetchSpecializations();
        await fetchCities();
      } catch (error) {
        showToast("error", error);
      } finally {
        dispatch({ type: "LOADER_STOP" });
      }
    };

    loadData();
  }, []);

  const onSubmit = async (data) => {
    try {
      dispatch({ type: "LOADER_START" });
      const res = await StoreConsultant(data);
      if (res.success) {
        showToast("success", res.message);
        navigate(`/masters/consultant/list`);
      } else {
        showToast("error", res.message);
      }
    } catch (err) {
      showToast("error", err.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "name", label: "Name", sample: "John Doe" },
    { key: "department", label: "Department", sample: "Cardiology" },
    { key: "consultantSpecialization", label: "Specialization", sample: "Cardiologist" },
    { key: "phone", label: "Phone", sample: "1234567890" },
    { key: "email", label: "Email", sample: "aDZ5W@example.com" },
    { key: "opdCharges", label: "OPD Charges", sample: "100" },
    { key: "casualtyCharges", label: "Consulty Charges", sample: "200" },
    { key: "opdValidUpTo", label: "OPD Valid Up To", sample: "30" },
    { key: "opdChargesBetweenValidityPeriod", label: "OPD Charges between Validity Period", sample: "10" },
    { key: "active", label: "Active", sample: "Yes" },
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportConsultant(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/consultant/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Consultant" title="Add Consultant" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Consultant</h5>
                <Link to={`/masters/consultant/list`}>
                  <button className="btn btn-primary" id="list">List Consultant</button>
                  <UncontrolledTooltip placement="top" target="list">List Consultant</UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* BASIC INFO */}
                    <div className="col-md-6 mb-3">
                      <label>Name *</label>
                      <input className="form-control" placeholder="Consultant Name" {...register("name")} />
                      {errors.name && <p className="text-danger">{errors.name.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Short Name</label>
                      <input className="form-control" placeholder="Short Name" {...register("shortName")} />
                      {errors.shortName && <p className="text-danger">{errors.shortName.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="nature">Nature *</label>
                      <select
                          className="form-control"
                          id="nature"
                          type="text"
                          placeholder="Nature"
                          {...register("nature")}
                        >
                          <option value="Consultant">Consultant</option>
                          <option value="Referred">Referred</option>
                          <option value="Both">Both</option>
                        </select>
                      {errors.nature && <p className="text-danger">{errors.nature.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Timing Form-To</label>
                      <div className="input-group">
                        <input
                          type="time"
                          className="form-control"
                          {...register("timingFrom")}
                        />
                        <input
                          type="time"
                          className="form-control"
                          {...register("timingTo")}
                        />
                      </div>
                    </div>

                    {/* RELATIONS */}
                    <div className="col-md-6 mb-3">
                      <label>Department *</label>
                      <select className="form-control" {...register("departmentId")}>
                        <option value="">Select Department</option>
                        {departments.map((dep) => (
                          <option key={dep._id} value={dep._id}>{dep.name}</option>
                        ))}
                      </select>
                      {errors.departmentId && <p className="text-danger">{errors.departmentId.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Specialization *</label>
                      <select className="form-control" {...register("consultantSpecializationId")}>
                        <option value="">Select Specialization</option>
                        {specializations.map((spec) => (
                          <option key={spec._id} value={spec._id}>{spec.name}</option>
                        ))}
                      </select>
                      {errors.consultantSpecializationId && <p className="text-danger">{errors.consultantSpecializationId.message}</p>}
                    </div>

                    {/* CONTACT */}
                    <div className="col-md-6 mb-3">
                      <label>Mobile No</label>
                      <input className="form-control" {...register("mobileNo")} />
                      {errors.mobileNo && <p className="text-danger">{errors.mobileNo.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Email</label>
                      <input className="form-control" {...register("email")} />
                      {errors.email && <p className="text-danger">{errors.email.message}</p>}
                    </div>

                    {/* PERSONAL INFO */}
                    <div className="col-md-6 mb-3">
                      <label>Date of Birth</label>
                      <input type="date" className="form-control" {...register("dob")} />
                      {errors.dob && <p className="text-danger">{errors.dob.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Anniversary</label>
                      <input type="date" className="form-control" {...register("anniversary")} />
                      {errors.anniversary && <p className="text-danger">{errors.anniversary.message}</p>}
                    </div>

                    {/* FINANCIALS */}
                    <div className="col-md-4 mb-3">
                      <label>OPD Charges</label>
                      <input type="number" step="any" className="form-control" {...register("opdCharges")} />
                      {errors.opdCharges && <p className="text-danger">{errors.opdCharges.message}</p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Casualty Charges</label>
                      <input type="number" step="any" className="form-control" {...register("casualtyCharges")} />
                      {errors.casualtyCharges && <p className="text-danger">{errors.casualtyCharges.message}</p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>OPD Valid Upto (days)</label>
                      <input type="number" step="any" className="form-control" {...register("opdValidUpTo")} />
                      {errors.opdValidUpTo && <p className="text-danger">{errors.opdValidUpTo.message}</p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>OPD Charges Between Validity Period</label>
                      <input type="number" step="any" className="form-control" {...register("opdChargesBetweenValidityPeriod")} />
                      {errors.opdChargesBetweenValidityPeriod && <p className="text-danger">{errors.opdChargesBetweenValidityPeriod.message}</p>}
                    </div>

                    {/* REGISTRATION INFO */}
                    <div className="col-md-6 mb-3">
                      <label>MCJ Registration No</label>
                      <input className="form-control" {...register("mcjRegNo")} />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>PAN No</label>
                      <input className="form-control" {...register("panNo")} />
                    </div>

                    {/* ACTIVE */}
                    <div className="col-md-6 mb-3">
                      <label>Status</label>
                      <select className="form-control" {...register("active")}>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>

                  {/* 🏥 CLINICS SECTION */}
                  <hr className="mt-4" />
                  <h5>Clinics</h5>

                  {fields.map((item, index) => (
                    <div key={item.id} className="border rounded p-3 mb-3">
                      <div className="row">
                        <div className="col-md-4">
                          <label>Site *</label>
                          <input className="form-control" {...register(`clinics.${index}.site`)} />
                          {errors.clinics?.[index]?.site && <p className="text-danger">{errors.clinics[index].site.message}</p>}
                        </div>

                        <div className="col-md-4">
                          <label>Timing From - To *</label>
                          <div className="input-group">
                            <input
                              type="time"
                              className="form-control"
                              {...register(`clinics.${index}.timingFrom`)}
                            />
                            <input
                              type="time"
                              className="form-control"
                              {...register(`clinics.${index}.timingTo`)}
                            />
                          </div>
                          {errors.clinics?.[index]?.timingFrom && <p className="text-danger">{errors.clinics[index].timingFrom.message}</p>}
                          {errors.clinics?.[index]?.timingTo && <p className="text-danger">{errors.clinics[index].timingTo.message}</p>}
                        </div>

                        <div className="col-md-4">
                          <label>Phone *</label>
                          <input className="form-control" {...register(`clinics.${index}.phone`)} />
                          {errors.clinics?.[index]?.phone && <p className="text-danger">{errors.clinics[index].phone.message}</p>}
                        </div>

                        <div className="col-md-4 mt-2">
                          <label>City *</label>
                          <select className="form-control" {...register(`clinics.${index}.cityId`)}>
                            <option value="">Select City</option>
                            {cities.map((city) => (
                              <option key={city._id} value={city._id}>
                                {city.name}
                              </option>
                            ))}
                          </select>
                          {errors.clinics?.[index]?.city && <p className="text-danger">{errors.clinics[index].city.message}</p>}
                        </div>

                        <div className="col-md-4 mt-2">
                          <label>Address 1 *</label>
                          <textarea className="form-control" {...register(`clinics.${index}.address1`)} />
                          {errors.clinics?.[index]?.address1 && <p className="text-danger">{errors.clinics[index].address1.message}</p>}
                        </div>

                        <div className="col-md-4 mt-2">
                          <label>Address 2</label>
                          <textarea className="form-control" {...register(`clinics.${index}.address2`)} />
                        </div>

                        <div className="col-md-4 mt-2">
                          <label>Address 3</label>
                          <textarea className="form-control" {...register(`clinics.${index}.address3`)} />
                        </div>

                        <div className="col-md-4 mt-4 d-flex align-items-end">
                          <button type="button" className="btn btn-danger" onClick={() => remove(index)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-secondary mb-3"
                    onClick={() =>
                      append({ site: "", timingFrom: "", timingTo: "", phone: "", cityId: "", address1: "", address2: "", address3: "" })
                    }
                  >
                    + Add Clinic
                  </button>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="consultants"
              sampleFileName="consultant_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddConsultant;
